import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useStore } from "../../../stores/store";
import { observer } from "mobx-react-lite";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useEditData } from "../../../hooks/useEditData";
import FormLayout from "../../Generic/FormLayout";
import FormInput from "../../Generic/FormInput";
import FormCheckbox from "../../Generic/FormCheckbox";
import { useSetMenu } from "../../../hooks/useSetMenu";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import * as yup from "yup";
import FormSelect from "../../Generic/FormSelect";
import FormFile from "../../Generic/FormFile";
import { useSnackbar } from "notistack";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const Edit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { mainStore } = useStore();
  const axiosPrivate = useAxiosPrivate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [logo, setLogo] = useState();
  const [logoName, setLogoName] = useState();
  const [logoPath, setLogoPath] = useState();

  useSetMenu(mainStore.ToolPassportMenuType, mainStore.ToolPassportMenuNo);

  // Extend your schema to include rich text fields
  const schema = yup.object({
    name: yup
      .string()
      .required(t("requiredField"))
      .max(250, t("maxLength", { max: 250 })),
    typeOfToolId: yup.string().required(t("selectRequired")),
    panelId: yup.string().required(t("selectRequired")),
    categoryId: yup.string().required(t("selectRequired")),
    subcategoryId: yup.string().required(t("selectRequired")),
    logoName: yup.string().nullable().max(250, t("maxLength", { max: 250 })),
    logoPath: yup.string().nullable().max(450, t("maxLength", { max: 450 })),
    // New rich text fields
    function: yup.string().required(t("requiredField")),
    abstractionLevel: yup.string().required(t("requiredField")),
    iOConnection: yup.string().required(t("requiredField")),
    verificationTool: yup.string().required(t("requiredField")),
    applicationAreaOrEquivalenceCoverage: yup
      .string()
      .required(t("requiredField")),
    qualitativeFeature: yup.string().required(t("requiredField")),
    quantitativeFeature: yup.string().required(t("requiredField")),
    // toolURL: yup.string().required(t('requiredField')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = (data) => {
    const formData = new FormData();
    Object.keys(data).forEach((fieldName) => {
      if (data[fieldName] != null) {
        formData.append(fieldName, data[fieldName]);
      }
    });
    formData.append("LogoName", logoName);
    formData.append("FormFile", logo);
    updateData(formData);
  };

  // States for dropdown options and selections
  const [typeOfTools, setTypeOfTools] = useState([]);
  const [selectedTypeOfTool, setSelectedTypeOfTool] = useState(null);
  const [panels, setPanels] = useState([]);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  // Fetch additional data and set default values including rich text fields
  const additionalDataFetch = async (toolPassportData) => {
    try {
      const panelsResponse = await axiosPrivate.get("/Panels/SelectOptions");
      setPanels(panelsResponse.data);
      const selPanel = panelsResponse.data.find(
        (c) => c.value === toolPassportData.panelId.toString()
      );
      setSelectedPanel(selPanel);

      const typeOfToolsResponse = await axiosPrivate.get(
        "/TypeOfTools/SelectOptions"
      );
      setTypeOfTools(typeOfToolsResponse.data);
      const selType = typeOfToolsResponse.data.find(
        (c) => c.value === toolPassportData.typeOfToolId.toString()
      );
      setSelectedTypeOfTool(selType);

      // Fetch categories based on panelId
      let categoriesResponse;
      if (selPanel) {
        categoriesResponse = await axiosPrivate.get(
          `/Categories/SelectOptionsByPanel/${selPanel.value}`
        );
        setCategories(categoriesResponse.data);
      }
      const selCategory = categoriesResponse.data.find(
        (c) => c.value === toolPassportData.categoryId.toString()
      );
      setSelectedCategory(selCategory);

      let subcategoriesResponse;
      if (selCategory) {
        subcategoriesResponse = await axiosPrivate.get(
          `/Subcategories/SelectOptionsByCategory/${selCategory.value}`
        );
        setSubcategories(subcategoriesResponse.data);
      }
      const selSubcategory = subcategoriesResponse.data.find(
        (c) => c.value === toolPassportData.subcategoryId.toString()
      );
      setSelectedSubcategory(selSubcategory);

      setLogoPath(toolPassportData.logo);
      setLogoName(toolPassportData.logoName);

      // Set rich text field values
      setValue("function", toolPassportData.function || "");
      setValue("abstractionLevel", toolPassportData.abstractionLevel || "");
      setValue("iOConnection", toolPassportData.ioConnection || "");
      setValue("verificationTool", toolPassportData.verificationTool || "");
      setValue(
        "applicationAreaOrEquivalenceCoverage",
        toolPassportData.applicationAreaOrEquivalenceCoverage || ""
      );
      setValue("qualitativeFeature", toolPassportData.qualitativeFeature || "");
      setValue("quantitativeFeature", toolPassportData.quantitativeFeature || "");
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleTypeOfToolChange = (selectedOption) => {
    setSelectedTypeOfTool(selectedOption);
    setValue("typeOfToolId", selectedOption.value, { shouldValidate: true });
  };

  const handlePanelChange = async (selectedOption) => {
    setValue("panelId", selectedOption.value, { shouldValidate: true });
    try {
      const response = await axiosPrivate.get(
        `/Categories/SelectOptionsByPanel/${selectedOption.value}`
      );
      if (typeof response.data === "string" && response.data === "No records") {
        setCategories([]);
      } else {
        setCategories(response.data);
      }

      setSelectedCategory(null);
      setValue("categoryId", null, { shouldValidate: true });
      setSubcategories([]);
      setSelectedSubcategory(null);
      setValue("subcategoryId", null, { shouldValidate: true });
    } catch (error) {
      enqueueSnackbar(t("errorLoadingCategories"), { variant: "error" });
    }
  };

  const handleCategoryChange = async (selectedOption) => {
    setValue("categoryId", selectedOption.value, { shouldValidate: true });
    setSelectedCategory(selectedOption);
    try {
      const response = await axiosPrivate.get(
        `/Subcategories/SelectOptionsByCategory/${selectedOption.value}`
      );
      if (typeof response.data === "string" && response.data === "No records") {
        setSubcategories([]);
        setSelectedSubcategory(null);
        setValue("subcategoryId", null, { shouldValidate: true });
      } else {
        setSubcategories(response.data);
        setSelectedSubcategory(null);
        setValue("subcategoryId", null, { shouldValidate: true });
      }
    } catch (error) {
      enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
    }
  };

  const handleSubcategoryChange = (selectedOption) => {
    setSelectedSubcategory(selectedOption);
    setValue("subcategoryId", selectedOption.value, { shouldValidate: true });
  };

  const { notFound, updateData } = useEditData(
    axiosPrivate,
    "/ToolPassports",
    id,
    setValue,
    navigate,
    additionalDataFetch
  );

  const goToList = () => {
    navigate("/ToolPassport/List");
  };

  if (notFound) {
    return (
      <div className="not-found-myDiv">
        <div className="not-found-container">
          <div className="not-found-content">
            <h1>{t("pageNotFoundTitle")}</h1>
            <p>{t("noDataWithIdMessage")}</p>
            <button onClick={goToList} className="btn btn-primary">
              {t("goTo") + " " + t("list") + " " + t("toolPassports")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const saveFile = (e) => {
    if (e.target.files[0]) {
      setLogo(e.target.files[0]);
      setLogoName(e.target.files[0].name);
      setValue("logoName", e.target.files[0].name);
    }
  };

  return (
    <FormLayout
      title={t("toolPassportEditTitle")}
      onSubmit={handleSubmit(onSubmit)}
      onCancel={() => navigate(-1)}
    >
      <div className="col-12 col-lg-12 d-flex justify-content-center">
        <img
          className="avatar img-fluid"
          src={
            logoPath
              ? mainStore.systemDomain + logoPath
              : "/assets/brand/default_img.png"
          }
          alt="Logo"
          loading="lazy"
          style={{ maxWidth: "150px", maxHeight: "150px" }}
        />
      </div>
      <FormFile
        label={logoPath ? t("uploadNewLogo") : t("uploadLogo")}
        name="logo"
        onChange={(e) => saveFile(e)}
        filePath={logoPath}
        fileName={logoName}
        register={register}
        required={false}
        errors={errors}
        colSize={12}
        type="file"
        accept="image/*"
      />
      <FormInput
        label="toolPassportName"
        name="name"
        register={register}
        errors={errors}
        colSize={6}
      />
      <FormSelect
        label="typeOfTool"
        name="typeOfToolId"
        options={typeOfTools}
        value={selectedTypeOfTool}
        colSize={6}
        onChange={handleTypeOfToolChange}
        register={register}
        errors={errors}
      />
      <FormInput label="toolPassportToolURL" name="toolURL" register={register} errors={errors} colSize={12} type="url" required={false}/>
      <FormSelect
        label="panel"
        name="panelId"
        register={register}
        value={selectedPanel}
        errors={errors}
        options={panels}
        onChange={handlePanelChange}
      />
      <FormSelect
        label="category"
        name="categoryId"
        register={register}
        errors={errors}
        options={categories}
        value={selectedCategory}
        onChange={handleCategoryChange}
      />
      <FormSelect
        label="subcategory"
        name="subcategoryId"
        register={register}
        errors={errors}
        options={subcategories}
        value={selectedSubcategory}
        onChange={handleSubcategoryChange}
      />
      <FormCheckbox label="isActive" register={register} name="isActive" colSize={4} />

      {/* ReactQuill Integration for Rich Text Fields */}
      <div className="form-group mt-2">
        <label className="labelDescription">
          <span className="labelText">{t("toolPassportFunctions")}</span>
          <span className="labelStar text-danger fw-bold">*</span>
        </label>
        <Controller
          name="function"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <ReactQuill theme="snow" value={value} onChange={onChange} />
          )}
        />
        {errors.function && (
          <div className="error-message text-danger">
            {errors.function.message}
          </div>
        )}
      </div>

      <div className="form-group mt-2">
        <label className="labelDescription">
          <span className="labelText">{t("toolPassportAbstractionLevel")}</span>
          <span className="labelStar text-danger fw-bold">*</span>
        </label>
        <Controller
          name="abstractionLevel"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <ReactQuill theme="snow" value={value} onChange={onChange} />
          )}
        />
        {errors.abstractionLevel && (
          <div className="error-message text-danger">
            {errors.abstractionLevel.message}
          </div>
        )}
      </div>

      <div className="form-group mt-2">
        <label className="labelDescription">
          <span className="labelText">{t("toolPassportIOConnection")}</span>
          <span className="labelStar text-danger fw-bold">*</span>
        </label>
        <Controller
          name="iOConnection"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <ReactQuill theme="snow" value={value} onChange={onChange} />
          )}
        />
        {errors.iOConnection && (
          <div className="error-message text-danger">
            {errors.iOConnection.message}
          </div>
        )}
      </div>

      <div className="form-group mt-2">
        <label className="labelDescription">
          <span className="labelText">{t("toolPassportVerificationTool")}</span>
          <span className="labelStar text-danger fw-bold">*</span>
        </label>
        <Controller
          name="verificationTool"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <ReactQuill theme="snow" value={value} onChange={onChange} />
          )}
        />
        {errors.verificationTool && (
          <div className="error-message text-danger">
            {errors.verificationTool.message}
          </div>
        )}
      </div>

      <div className="form-group mt-2">
        <label className="labelDescription">
          <span className="labelText">
            {t("toolPassportApplicationAreaOrEquivalenceCoverage")}
          </span>
          <span className="labelStar text-danger fw-bold">*</span>
        </label>
        <Controller
          name="applicationAreaOrEquivalenceCoverage"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <ReactQuill theme="snow" value={value} onChange={onChange} />
          )}
        />
        {errors.applicationAreaOrEquivalenceCoverage && (
          <div className="error-message text-danger">
            {errors.applicationAreaOrEquivalenceCoverage.message}
          </div>
        )}
      </div>

      <div className="form-group mt-2">
        <label className="labelDescription">
          <span className="labelText">{t("toolPassportQualitativeFeature")}</span>
          <span className="labelStar text-danger fw-bold">*</span>
        </label>
        <Controller
          name="qualitativeFeature"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <ReactQuill theme="snow" value={value} onChange={onChange} />
          )}
        />
        {errors.qualitativeFeature && (
          <div className="error-message text-danger">
            {errors.qualitativeFeature.message}
          </div>
        )}
      </div>

      <div className="form-group mt-2">
        <label className="labelDescription">
          <span className="labelText">{t("toolPassportQuantitativeFeature")}</span>
          <span className="labelStar text-danger fw-bold">*</span>
        </label>
        <Controller
          name="quantitativeFeature"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <ReactQuill theme="snow" value={value} onChange={onChange} />
          )}
        />
        {errors.quantitativeFeature && (
          <div className="error-message text-danger">
            {errors.quantitativeFeature.message}
          </div>
        )}
      </div>
    </FormLayout>
  );
};

export default observer(Edit);

// import React, { useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import { useForm } from "react-hook-form";
// import { useStore } from "../../../stores/store";
// import { observer } from "mobx-react-lite";
// import { yupResolver } from "@hookform/resolvers/yup";
// import { useTranslation } from "react-i18next";
// import { useEditData } from "../../../hooks/useEditData";
// import FormLayout from '../../Generic/FormLayout';
// import FormInput from '../../Generic/FormInput';
// import FormCheckbox from '../../Generic/FormCheckbox';
// import { useSetMenu } from "../../../hooks/useSetMenu";
// import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
// import * as yup from "yup";
// import FormSelect from '../../Generic/FormSelect';
// import FormFile from "../../Generic/FormFile";
// import { useSnackbar } from "notistack";

// const Edit = () => {
//     const { id } = useParams();
//     const navigate = useNavigate();
//     const { mainStore } = useStore();
//     const axiosPrivate = useAxiosPrivate();
//     const { t } = useTranslation();
//     const { enqueueSnackbar } = useSnackbar();
//     const [logo, setLogo] = useState();
//     const [logoName, setLogoName] = useState();
//     const [logoPath, setLogoPath] = useState();

//     useSetMenu(mainStore.ToolPassportMenuType, mainStore.ToolPassportMenuNo);
//     const schema = yup.object({
//         name: yup
//             .string()
//             .required(t('requiredField'))
//             .max(250, t('maxLength', { max: 250 })),
//         typeOfToolId: yup.string().required(t("selectRequired")),
//         panelId: yup.string().required(t("selectRequired")),
//         categoryId: yup.string().required(t("selectRequired")),
//         subcategoryId: yup.string().required(t("selectRequired")),
//         logoName: yup
//             .string()
//             .nullable()
//             .max(250, t('maxLength', { max: 250 })),
//         logoPath: yup
//             .string()
//             .nullable()
//             .max(450, t('maxLength', { max: 450 })),
//     });

//     const { register, handleSubmit, formState: { errors }, setValue } = useForm({ resolver: yupResolver(schema) });
//     const onSubmit = (data) => {

//         const formData = new FormData();
//         Object.keys(data).forEach((fieldName) => {

//             if (data[fieldName] != null) {
//                 formData.append(fieldName, data[fieldName]);
//             }
//         });
//         formData.append("LogoName", logoName);
//         formData.append("FormFile", logo);


//         updateData(formData);
//     }

//     const [typeOfTools, setTypeOfTools] = useState([]);
//     const [selectedTypeOfTool, setSelectedTypeOfTool] = useState(null);

//     const [panels, setPanels] = useState([]);
//     const [selectedPanel, setSelectedPanel] = useState(null);


//     const [subcategories, setSubcategories] = useState([]);
//     const [selectedSubcategory, setSelectedSubcategory] = useState(null);

//     const [categories, setCategories] = useState([]);
//     const [selectedCategory, setSelectedCategory] = useState(null);

//     const additionalDataFetch = async (toolPassportData) => {
//         try {
//             const panelsResponse = await axiosPrivate.get("/Panels/SelectOptions");
//             setPanels(panelsResponse.data);
//             const selectedPanel = panelsResponse.data.find(c => c.value === toolPassportData.panelId.toString());
//             setSelectedPanel(selectedPanel);

//             const typeOfToolsResponse = await axiosPrivate.get("/TypeOfTools/SelectOptions");
//             setTypeOfTools(typeOfToolsResponse.data);
//             const selectedTypeOfTool = typeOfToolsResponse.data.find(c => c.value === toolPassportData.typeOfToolId.toString());
//             setSelectedTypeOfTool(selectedTypeOfTool);


//             // Fetch categories based on panelId
//             let categoriesResponse;
//             if (selectedPanel) {
//                 categoriesResponse = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${selectedPanel.value}`);
//                 setCategories(categoriesResponse.data);
//             }

//             const selectedCategory = categoriesResponse.data.find(c => c.value === toolPassportData.categoryId.toString());
//             setSelectedCategory(selectedCategory);

//             let subcategoriesResponse;
//             if (selectedCategory) {
//                 subcategoriesResponse = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${selectedCategory.value}`);
//                 setSubcategories(subcategoriesResponse.data);
//             }

//             const selectedSubcategory = subcategoriesResponse.data.find(c => c.value === toolPassportData.subcategoryId.toString());
//             setSelectedSubcategory(selectedSubcategory);

//             setLogoPath(toolPassportData.logo);
//             setLogoName(toolPassportData.logoName);
//         } catch (error) {
//             console.error('Error fetching data:', error);
//         }
//     };


//     const handleTypeOfToolChange = selectedOption => {
//         setSelectedTypeOfTool(selectedOption);
//         setValue("typeOfToolId", selectedOption.value, { shouldValidate: true });
//     };

//     const handlePanelChange = async (selectedOption) => {
//         setValue("panelId", selectedOption.value, { shouldValidate: true });
//         try {
//             const response = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${selectedOption.value}`);
//             if (typeof response.data === "string" && response.data === "No records") {
//                 setCategories([]);
//                 setSelectedCategory(null);
//                 setValue("categoryId", null, { shouldValidate: true });
//                 setSubcategories([]);
//                 setSelectedSubcategory(null);
//                 setValue("subcategoryId", null, { shouldValidate: true });
//             } else {
//                 setCategories(response.data);
//                 setSelectedCategory(null);
//                 setValue("categoryId", null, { shouldValidate: true });
//                 setSubcategories([]);
//                 setSelectedSubcategory(null);
//                 setValue("subcategoryId", null, { shouldValidate: true });
//             }
//         } catch (error) {
//             enqueueSnackbar(t("errorLoadingCategories"), { variant: "error" });
//         }
//     };

//     const handleCategoryChange = async (selectedOption) => {
//         setValue("categoryId", selectedOption.value, { shouldValidate: true });
//         setSelectedCategory(selectedOption);
//         try {
//             const response = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${selectedOption.value}`);
//             if (typeof response.data === "string" && response.data === "No records") {
//                 setSubcategories([]);
//                 setSelectedSubcategory(null);
//                 setValue("subcategoryId", null, { shouldValidate: true });
//             } else {
//                 setSubcategories(response.data);
//                 setSelectedSubcategory(null);
//                 setValue("subcategoryId", null, { shouldValidate: true });
//             }
//         } catch (error) {
//             enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
//         }
//     };

//     const handleSubcategoryChange = selectedOption => {
//         setSelectedSubcategory(selectedOption);
//         setValue("subcategoryId", selectedOption.value, { shouldValidate: true });
//     };

//     const { notFound, updateData } = useEditData(axiosPrivate, "/ToolPassports", id, setValue, navigate, additionalDataFetch);

//     const goToList = () => {
//         navigate("/ToolPassport/List");
//     };

//     if (notFound) {
//         return <div className="not-found-myDiv">
//             <div className="not-found-container">
//                 <div className="not-found-content">
//                     <h1>{t("pageNotFoundTitle")}</h1>
//                     <p>{t("noDataWithIdMessage")}</p>
//                     <button onClick={goToList} className="btn btn-primary">
//                         {t("goTo") + " " + t("list") + " " + t("cities")}
//                     </button>
//                 </div>
//             </div>
//         </div>;
//     }

//     const saveFile = (e) => {
//         if (e.target.files[0]) {
//             setLogo(e.target.files[0]);
//             setLogoName(e.target.files[0].name);
//             setValue("logoName", e.target.files[0].name);
//         }
//     };

//     return (
//         <FormLayout title={t("toolPassportEditTitle")} onSubmit={handleSubmit(onSubmit)} onCancel={() => navigate(-1)}>
//             <div className="col-12 col-lg-12 d-flex justify-content-center">
//                 <img
//                     className="avatar img-fluid"
//                     src={logoPath ? mainStore.systemDomain + logoPath : "/assets/brand/default_img.png"}
//                     alt="Logo"
//                     loading="lazy"
//                     style={{ maxWidth: "150px", maxHeight: "150px" }}
//                 />
//             </div>
//             <FormFile label={logoPath ? t("uploadNewLogo") : t("uploadLogo")} name="logo" onChange={(e) => saveFile(e)} filePath={logoPath} fileName={logoName} register={register} required={false} errors={errors} colSize={12} type="file" accept="image/*" />
//             <FormInput label="toolPassportName" name="name" register={register} errors={errors} colSize={6}/>
//             <FormSelect label="typeOfTool" name="typeOfToolId" options={typeOfTools} value={selectedTypeOfTool} colSize={6} onChange={handleTypeOfToolChange} register={register} errors={errors} />
//             <FormSelect label="panel" name="panelId" register={register} value={selectedPanel} errors={errors}  options={panels} onChange={handlePanelChange} />
//             <FormSelect label="category" name="categoryId" register={register} errors={errors} options={categories} value={selectedCategory} onChange={handleCategoryChange} />
//             <FormSelect label="subcategory" name="subcategoryId" register={register} errors={errors} options={subcategories} value={selectedSubcategory} onChange={handleSubcategoryChange} />
//             <FormCheckbox label="isActive" register={register} name="isActive" colSize={4} />

//         </FormLayout>
//     );
// };

// export default observer(Edit);